import { createAsyncThunk } from '@reduxjs/toolkit';
import get from 'lodash/get';
import { GetCartItemsAmount, GetCart, AddMultipleSimpleProducts, AddConfigurableProduct, UpdateItemQuantity, RemoveItem } from '../../services/graphql/cart/index.js';
import { a as privateQuery } from '../../requestsCollection-A0HzS4ai.js';
import { checkCartSessionIdValidity, getOrCreateCartSessionId } from '../../utils/cart.js';
import { extractErrorMessage } from '../../utils/error.js';
import logger from '../../utils/logger.js';
import { getCookie } from '../../utils/cookies.js';
import '../../utils/currency.js';
import 'react';
import '../../context/index.js';
import { setUserCart } from '../../utils/salesforce.js';
import '../../product-_pRduMz7.js';
import 'next/router';
import { A as AccorGtmCartTracking } from '../../helpers-WYINLSKJ.js';
import { g as COOKIE_CART_SESSION_ID, a as COOKIE_LANGUAGE_NAME } from '../../cookies-VX_DpI1q.js';
import '../../currency-_kacFJHO.js';
import '../../language-BHHwgtQp.js';
import '../../payment-a4IKjS7g.js';
import { d as dataLayerCartAccor, s as sendCartEventAccor } from '../../cart-RpjAis5B.js';
import 'lodash';
import { d as dataLayerSelectItemAccor } from '../../selectItem-AV_wgSCj.js';
import { d as dataLayerCart, s as sendCartEvent } from '../../cart-bEN0otWy.js';
import 'lodash/cloneDeep';
import '../../MergeCarts-wo9UjKo5.js';
import 'graphql-request';
import '../../services/auth/index.js';
import '../../utils/logToSlack.js';
import '../../utils/fetchPolicyLookup.js';
import '../../ProcessReactivateCart-fk5bSKoR.js';
import '../../utils/product.js';
import '../../paths-ZKN3m62_.js';
import '../../utils/price.js';
import 'number-currency-format';
import '../../cart-AU0vdOfm.js';
import '../../serverErrors-O6unZmFR.js';
import 'cookies-next';
import '../../utils/isBot.js';
import '../../utils/logToRelic.js';

const locale = getCookie(COOKIE_LANGUAGE_NAME);
const getCartItemsAmount = createAsyncThunk('cart/getCartItemsAmount', async (_, { extra, rejectWithValue }) => {
    try {
        const { apolloConfig } = extra;
        const cartSessionId = String(getCookie(COOKIE_CART_SESSION_ID) || '');
        if (!cartSessionId)
            return 0;
        const { data, errors } = await privateQuery(apolloConfig(), {
            query: GetCartItemsAmount,
            variables: { cart_id: cartSessionId, custom_duty: false },
        }, 'GetCartItemsAmount');
        const errorMessage = get(errors, [0, 'message'], '');
        if (errorMessage) {
            checkCartSessionIdValidity(errorMessage);
            return rejectWithValue({
                errorMessage,
            });
        }
        logger('getCartItemsAmount', { category: 'api' });
        return data.cart.total_quantity;
    }
    catch (e) {
        return rejectWithValue({
            errorMessage: extractErrorMessage(e),
        });
    }
});
const getCart = createAsyncThunk('cart/getCart', async ({ cart_id, accorTaggingEnabled }, { extra, rejectWithValue }) => {
    try {
        const { apolloConfig, siteConfig } = extra;
        const { data, errors } = await privateQuery(apolloConfig(), {
            query: GetCart,
            variables: {
                cart_id,
                custom_duty: false,
            },
        }, 'GetCart');
        const errorMessage = get(errors, [0, 'message'], '');
        if (errorMessage) {
            checkCartSessionIdValidity(errorMessage);
            return rejectWithValue({
                errorMessage,
            });
        }
        if (typeof window !== 'undefined') {
            if (accorTaggingEnabled) {
                dataLayerCartAccor({
                    items: data.cart.items,
                    grandTotal: data.cart.prices.subtotal_including_tax.value,
                    currencyCode: data.cart.prices.subtotal_including_tax.currency,
                    siteFilters: siteConfig.productNameFilters,
                });
            }
            else {
                dataLayerCart({
                    items: data.cart.items,
                    locale,
                    grandTotal: data.cart.prices.subtotal_including_tax.value,
                    currencyCode: data.cart.prices.subtotal_including_tax.currency,
                    siteFilters: siteConfig.productNameFilters,
                });
            }
        }
        logger('getCart', { category: 'api' });
        return data.cart;
    }
    catch (e) {
        return rejectWithValue({
            errorMessage: extractErrorMessage(e),
        });
    }
});
const addProduct = createAsyncThunk('cart/addProduct', async ({ simpleProducts = [], configurableProducts = [], accorTaggingEnabled, itemListName }, { extra, rejectWithValue }) => {
    var _a, _b;
    try {
        const { apolloConfig, siteConfig, graphql } = extra;
        // Use "AddMultipleSimpleProducts" from MainApp or from CorePackage
        const AddMultipleSimpleProductsGraphql = ((_a = graphql === null || graphql === void 0 ? void 0 : graphql.cart) === null || _a === void 0 ? void 0 : _a.AddMultipleSimpleProducts) || AddMultipleSimpleProducts;
        const AddConfigurableProductGraphql = ((_b = graphql === null || graphql === void 0 ? void 0 : graphql.cart) === null || _b === void 0 ? void 0 : _b.AddConfigurableProduct) || AddConfigurableProduct;
        const cartSessionId = await getOrCreateCartSessionId(apolloConfig);
        let addProductResponse = null;
        if (configurableProducts.length) {
            const { data, errors } = await privateQuery(apolloConfig(), {
                query: AddConfigurableProductGraphql,
                variables: {
                    cartId: cartSessionId,
                    cartItems: configurableProducts,
                },
            }, 'AddConfigurableProduct');
            const errorMessage = get(errors, [0, 'message'], '');
            if (errorMessage) {
                checkCartSessionIdValidity(errorMessage);
                return rejectWithValue({
                    errorMessage,
                });
            }
            const addConfigurableProductsResponse = data.addConfigurableProductsToCart.cart;
            addProductResponse = addConfigurableProductsResponse;
        }
        if (simpleProducts.length) {
            const { data, errors } = await privateQuery(apolloConfig(), {
                query: AddMultipleSimpleProductsGraphql,
                variables: {
                    cartId: cartSessionId,
                    cartItems: simpleProducts,
                },
            }, 'AddMultipleSimpleProducts');
            const errorMessage = get(errors, [0, 'message'], '');
            if (errorMessage) {
                checkCartSessionIdValidity(errorMessage);
                return rejectWithValue({
                    errorMessage,
                });
            }
            const addSimpleProductsResponse = data.addSimpleProductsToCart.cart;
            if (addSimpleProductsResponse) {
                if (addProductResponse) {
                    addProductResponse = {
                        ...addSimpleProductsResponse,
                        item_selected: [
                            ...addProductResponse.item_selected,
                            ...addSimpleProductsResponse.item_selected,
                        ],
                        ...addSimpleProductsResponse.items,
                    };
                }
                else {
                    addProductResponse = addSimpleProductsResponse;
                }
            }
        }
        if (accorTaggingEnabled) {
            AccorGtmCartTracking.updateCart({
                items: addProductResponse.item_selected || addProductResponse.items,
                itemListName,
                op: 'add',
            });
            sendCartEventAccor({
                cart: addProductResponse,
                siteFilters: siteConfig.productNameFilters,
                affiliation: siteConfig.parentHotelCompany,
                brand: siteConfig.siteName,
            });
        }
        else {
            sendCartEvent({
                cart: addProductResponse,
                locale,
                siteFilters: siteConfig.productNameFilters,
                affiliation: siteConfig.parentHotelCompany,
                brand: siteConfig.siteName,
            });
        }
        setUserCart(addProductResponse);
        return addProductResponse;
    }
    catch (e) {
        return rejectWithValue({
            errorMessage: extractErrorMessage(e),
        });
    }
});
const updateItemQuantity = createAsyncThunk('cart/updateItemQuantity', async ({ itemListName, accorTaggingEnabled, ...payload }, { extra, rejectWithValue }) => {
    try {
        const { apolloConfig, siteConfig } = extra;
        const { data, errors } = await privateQuery(apolloConfig(), {
            query: UpdateItemQuantity,
            variables: {
                ...payload,
            },
        }, 'UpdateItemQuantity');
        if (errors === null || errors === void 0 ? void 0 : errors.length) {
            return rejectWithValue({
                errorMessage: errors[0].message,
            });
        }
        if (typeof window !== 'undefined') {
            if (accorTaggingEnabled) {
                dataLayerCartAccor({
                    items: data.updateCartItems.cart.items,
                    grandTotal: data.updateCartItems.cart.prices.subtotal_including_tax.value,
                    currencyCode: data.updateCartItems.cart.prices.subtotal_including_tax
                        .currency,
                    siteFilters: siteConfig.productNameFilters,
                });
            }
            else {
                dataLayerCart({
                    items: data.updateCartItems.cart.items,
                    locale,
                    grandTotal: data.updateCartItems.cart.prices.subtotal_including_tax.value,
                    currencyCode: data.updateCartItems.cart.prices.subtotal_including_tax
                        .currency,
                    siteFilters: siteConfig.productNameFilters,
                });
            }
        }
        if (accorTaggingEnabled) {
            const updatedItem = data.updateCartItems.cart.items.find(el => el.id === String(payload.cart_item_id));
            AccorGtmCartTracking.updateCart({
                items: [updatedItem],
                itemListName,
                op: 'add',
            });
            sendCartEventAccor({
                cart: data.updateCartItems.cart,
                siteFilters: siteConfig.productNameFilters,
                affiliation: siteConfig.parentHotelCompany,
                brand: siteConfig.siteName,
            });
            dataLayerSelectItemAccor({
                product: updatedItem,
                affiliation: siteConfig.parentHotelCompany,
                brand: siteConfig.siteName,
                index: 0,
                itemListName: 'mini_basket',
            });
        }
        else {
            sendCartEvent({
                cart: data.updateCartItems.cart,
                locale,
                siteFilters: siteConfig.productNameFilters,
                affiliation: siteConfig.parentHotelCompany,
                brand: siteConfig.siteName,
            });
        }
        setUserCart(data.updateCartItems.cart);
        return data.updateCartItems.cart;
    }
    catch (e) {
        return rejectWithValue({
            errorMessage: extractErrorMessage(e),
        });
    }
});
const removeItem = createAsyncThunk('cart/removeItem', async (payload, { extra, rejectWithValue }) => {
    try {
        const { apolloConfig, siteConfig } = extra;
        const { data, errors } = await privateQuery(apolloConfig(), {
            query: RemoveItem,
            variables: {
                ...payload,
            },
        }, 'RemoveItem');
        if (errors === null || errors === void 0 ? void 0 : errors.length) {
            return rejectWithValue({
                errorMessage: errors[0].message,
            });
        }
        if (typeof window !== 'undefined') {
            if (payload.accorTaggingEnabled) {
                try {
                    dataLayerCartAccor({
                        items: data.removeItemFromCart.cart.items,
                        grandTotal: data.removeItemFromCart.cart.prices.subtotal_including_tax.value,
                        currencyCode: data.removeItemFromCart.cart.prices.subtotal_including_tax
                            .currency,
                        siteFilters: siteConfig.productNameFilters,
                    });
                    dataLayerSelectItemAccor({
                        product: payload.cart_item,
                        affiliation: siteConfig.parentHotelCompany,
                        brand: siteConfig.siteName,
                        index: 0,
                        itemListName: 'mini_basket',
                    });
                    AccorGtmCartTracking.updateCart({
                        items: [payload.cart_item],
                        op: 'remove',
                    });
                }
                catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
            else {
                dataLayerCart({
                    items: data.removeItemFromCart.cart.items,
                    locale,
                    grandTotal: data.removeItemFromCart.cart.prices.subtotal_including_tax.value,
                    currencyCode: data.removeItemFromCart.cart.prices.subtotal_including_tax
                        .currency,
                    siteFilters: siteConfig.productNameFilters,
                });
            }
        }
        // prepare data for sendCart event
        const removeCart = { ...data.removeItemFromCart.cart };
        const newCartItem = { ...payload.cart_item, just_removed_qty: payload.cart_item.quantity };
        removeCart.items = [newCartItem];
        if (payload.accorTaggingEnabled) {
            sendCartEventAccor({
                cart: removeCart,
                siteFilters: siteConfig.productNameFilters,
                affiliation: siteConfig.parentHotelCompany,
                brand: siteConfig.siteName,
            });
        }
        else {
            sendCartEvent({
                cart: removeCart,
                locale,
                siteFilters: siteConfig.productNameFilters,
                affiliation: siteConfig.parentHotelCompany,
                brand: siteConfig.siteName,
            });
        }
        setUserCart(data.removeItemFromCart.cart);
        return data.removeItemFromCart.cart;
    }
    catch (e) {
        return rejectWithValue({
            errorMessage: extractErrorMessage(e),
        });
    }
});

export { addProduct, getCart, getCartItemsAmount, removeItem, updateItemQuantity };
//# sourceMappingURL=thunks.js.map
